// Browser storage service for user identification and session management
// This allows users to return to the funnel without needing shortcodes in URLs

const STORAGE_KEYS = {
  USER_SESSION: 'optimalPace_userSession',
  FUNNEL_CACHE: 'optimalPace_funnelCache'
};

// Check if localStorage is available
const isLocalStorageAvailable = () => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
};

// Get user session from browser storage
export const getUserSessionFromStorage = () => {
  if (!isLocalStorageAvailable()) {
    console.warn('LocalStorage not available, falling back to Firebase-only mode');
    return null;
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEYS.USER_SESSION);
    if (stored) {
      const session = JSON.parse(stored);
      
      // Validate that the session has required fields
      if (session.shortcode && session.startTime) {
        return session;
      }
    }
  } catch (error) {
    console.error('Error reading user session from storage:', error);
  }
  
  return null;
};

// Save user session to browser storage
export const saveUserSessionToStorage = (session) => {
  if (!isLocalStorageAvailable()) {
    console.warn('LocalStorage not available, cannot save session');
    return false;
  }

  try {
    // Only save essential data to localStorage to keep it lightweight
    const sessionToStore = {
      shortcode: session.shortcode,
      startTime: session.startTime,
      lastActive: session.lastActive,
      currentScreen: session.currentScreen,
      completedScreens: session.completedScreens || [],
      userInfo: session.userInfo || {},
      // Don't store large data like journalEntries, responses, calculatedMetrics
      // These will be loaded from Firebase when needed
    };
    
    localStorage.setItem(STORAGE_KEYS.USER_SESSION, JSON.stringify(sessionToStore));
    return true;
  } catch (error) {
    console.error('Error saving user session to storage:', error);
    return false;
  }
};

// Clear user session from browser storage
export const clearUserSessionFromStorage = () => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    localStorage.removeItem(STORAGE_KEYS.USER_SESSION);
    return true;
  } catch (error) {
    console.error('Error clearing user session from storage:', error);
    return false;
  }
};

// Clear all funnel-related data from browser storage
export const clearAllFunnelStorage = () => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    localStorage.removeItem(STORAGE_KEYS.USER_SESSION);
    localStorage.removeItem(STORAGE_KEYS.FUNNEL_CACHE);
    return true;
  } catch (error) {
    console.error('Error clearing all funnel storage:', error);
    return false;
  }
};

const FUNNEL_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days — ad-entry content rarely changes

const readFunnelCacheRaw = () => {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEYS.FUNNEL_CACHE);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error reading funnel cache from storage:', error);
    return null;
  }
};

// Get funnel cache from browser storage
export const getFunnelCacheFromStorage = () => {
  try {
    const cache = readFunnelCacheRaw();
    if (!cache) return null;

    if (cache.lastUpdated && Date.now() - cache.lastUpdated < FUNNEL_CACHE_TTL_MS) {
      return cache;
    }
  } catch (error) {
    console.error('Error reading funnel cache from storage:', error);
  }

  return null;
};

// Save funnel cache to browser storage
export const saveFunnelCacheToStorage = (cache) => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    const existing = readFunnelCacheRaw() || {};
    const cacheToStore = {
      ...existing,
      ...cache,
      lastUpdated: Date.now(),
    };

    localStorage.setItem(STORAGE_KEYS.FUNNEL_CACHE, JSON.stringify(cacheToStore));
    return true;
  } catch (error) {
    console.error('Error saving funnel cache to storage:', error);
    return false;
  }
};

/** Cached checkpoint document by screen key (e.g. "who") */
export const getCachedCheckpoint = (key) => {
  const cache = getFunnelCacheFromStorage();
  const entry = cache?.checkpoints?.[key];
  return entry && typeof entry === 'object' ? entry : null;
};

export const saveCachedCheckpoint = (key, data) => {
  if (!key || !data) return false;
  const cache = readFunnelCacheRaw() || {};
  return saveFunnelCacheToStorage({
    ...cache,
    checkpoints: {
      ...(cache.checkpoints || {}),
      [key]: data,
    },
  });
};

/** Cached configuration items by doc id (e.g. "types") */
export const getCachedConfigItems = (docId) => {
  const cache = getFunnelCacheFromStorage();
  const entry = cache?.config?.[docId];
  return Array.isArray(entry) ? entry : null;
};

export const saveCachedConfigItems = (docId, items) => {
  if (!docId || !Array.isArray(items)) return false;
  const cache = readFunnelCacheRaw() || {};
  return saveFunnelCacheToStorage({
    ...cache,
    config: {
      ...(cache.config || {}),
      [docId]: items,
    },
  });
};

// Check if we have a valid user session in browser storage
export const hasValidUserSession = () => {
  const session = getUserSessionFromStorage();
  return session !== null;
};

// Get the user's shortcode from browser storage
export const getShortcodeFromStorage = () => {
  const session = getUserSessionFromStorage();
  return session ? session.shortcode : null;
};

// Save the current URL path to browser storage
export const saveCurrentPathToStorage = (path) => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    localStorage.setItem('optimalPace_lastPath', path);
    return true;
  } catch (error) {
    console.error('Error saving current path to storage:', error);
    return false;
  }
};

// Get the last visited path from browser storage
export const getLastPathFromStorage = () => {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    return localStorage.getItem('optimalPace_lastPath');
  } catch (error) {
    console.error('Error reading last path from storage:', error);
    return null;
  }
};

// Check if app is running in standalone mode (added to home screen)
export const isStandaloneMode = () => {
  // Check for iOS standalone mode
  if (window.navigator.standalone === true) {
    return true;
  }
  
  // Check for Android standalone mode
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  // Check for other PWA indicators
  if (window.matchMedia && window.matchMedia('(display-mode: fullscreen)').matches) {
    return true;
  }
  
  return false;
};
